import { assert } from '@ember/debug';
import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import {
  addDays,
  addMonths,
  differenceInCalendarDays,
  endOfDay,
  getUnixTime,
  isSameDay,
  startOfDay,
  subMonths,
} from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import { dropTask } from 'ember-concurrency';
import PowerSelectMultiple from 'ember-power-select/components/power-select-multiple';
import { or } from 'ember-truth-helpers';
import CalendarGrid from 'garaje/components/calendar-grid';
import ModalDialog from 'garaje/components/ui/classic/modal-dialog';
import type ActiveDayConfig from 'garaje/types/date-time/active-day-config';
import type DateLimits from 'garaje/types/date-time/date-limits';
import type DayConfig from 'garaje/types/date-time/day-config';
import { DEFAULT_TIMEZONE, modifyDateInTimeZone, parseYyyyMmDdInTimeZone } from 'garaje/utils/date-fns-tz-utilities';
import { cached } from 'tracked-toolbox';

export type MultiDateScheduleModalMode = 'schedule' | 'admin-lend' | 'admin-cancel-lending';

interface DayData {
  date: Date;
  screeningCard?: { id: string } | null;
  reservations?: unknown[];
  reservation?: unknown;
}

interface LendingStatus {
  date: string;
  lendingStatus: string;
  reservation?: {
    reservationId: number;
    bookedByEmployeeId: number;
    bookedByName: string;
    bookedByEmail: string;
    startTime: string;
    endTime: string;
  } | null;
}

interface LendableDeskInfo {
  deskId: number;
  deskName: string;
  floorName: string;
  dates: unknown[];
}

interface DeskOption {
  deskId: string;
  label: string;
}

interface MultiDateScheduleModalSignature {
  Args: {
    onClose: () => void;
    onSchedule: (dates: string[]) => Promise<void>;
    onLend: (dates: string[], deskIds?: string[]) => Promise<void>;
    onCancelLend?: (dates: string[]) => Promise<void>;
    lendingStatusByDate: Record<string, LendingStatus>;
    deskLendingEnabled: boolean;
    bookDeskEnabled: boolean;
    days: DayData[];
    activeDays?: ActiveDayConfig[];
    multiDeskEnabled?: boolean;
    lendableDesks?: LendableDeskInfo[];
    lendingStatusByDeskAndDate?: Record<string, Record<string, LendingStatus>>;
    title?: string;
    submitLabel?: string;
    mode?: MultiDateScheduleModalMode;
    allowedDates?: string[];
    // Mode-specific: only honored when mode === 'admin-lend'. Caller passes
    // dates the desk is already lent on so the calendar disables them.
    adminLendDisabledDates?: string[];
    initialDates?: Date[];
    locationTimeZone?: string;
  };
}

export default class MultiDateScheduleModal extends Component<MultiDateScheduleModalSignature> {
  // Seeded once on construction — @initialDates updates after mount are
  // intentionally ignored. Every caller today renders the modal inside a
  // {{#if}} wrapper, so "re-open" is always a fresh mount and the latest
  // @initialDates is picked up anyway.
  //
  // Callers in admin-cancel-lending mode must pre-filter initialDates
  // against @allowedDates — the modal does NOT defensively filter here, so
  // a date seeded into selectedDates that isn't in @allowedDates will
  // render as a chip the calendar refuses to unselect or re-add. Today's
  // sole caller (desk-popup.adminCancelLendingInitialDates) enforces this.
  @tracked selectedDates: Date[] = this.args.initialDates ? [...this.args.initialDates] : [];
  @tracked currentMonth = new Date();
  @tracked calendarOpen = false;
  @tracked showDeskSelection = false;
  @tracked selectedDesks: DeskOption[] = [];

  get effectiveTimeZone(): string {
    return this.args.locationTimeZone ?? DEFAULT_TIMEZONE;
  }

  // Unix ts representing start-of-today in the effective timezone.
  get locationTodayUnix(): number {
    return getUnixTime(modifyDateInTimeZone(new Date(), this.effectiveTimeZone, startOfDay));
  }

  // yyyy-MM-dd key for today in the effective timezone.
  get locationTodayKey(): string {
    return this.formatDateKey(new Date());
  }

  formatDateKey(date: Date): string {
    return formatInTimeZone(date, this.effectiveTimeZone, 'yyyy-MM-dd');
  }

  // Highest key the admin can still select in admin-cancel-lending mode.
  // Derived entirely from @allowedDates so we don't drift from the backend's
  // current lending state for this desk. Callers (AMP-2834) only open the modal
  // when the desk has at least one lent date; if allowedDates is empty we bound
  // the calendar to today so nothing beyond is reachable.
  get cancelLendingMaxKey(): string {
    const allowed = this.args.allowedDates ?? [];
    if (allowed.length === 0) return this.locationTodayKey;
    // yyyy-MM-dd sorts lexicographically = chronologically.
    return allowed.slice().sort().at(-1)!;
  }

  get dateLimits(): DateLimits {
    const limits: DateLimits = { startDate: this.locationTodayUnix };
    if (this.isCancelLendingMode) {
      const maxDate = parseYyyyMmDdInTimeZone(this.cancelLendingMaxKey, new Date(), this.effectiveTimeZone);
      limits.endDate = getUnixTime(modifyDateInTimeZone(maxDate, this.effectiveTimeZone, endOfDay));
    }
    return limits;
  }

  get mode(): MultiDateScheduleModalMode {
    return this.args.mode ?? 'schedule';
  }

  get isAdminMode(): boolean {
    return this.mode === 'admin-lend' || this.mode === 'admin-cancel-lending';
  }

  get isCancelLendingMode(): boolean {
    return this.mode === 'admin-cancel-lending';
  }

  get isAdminLendMode(): boolean {
    return this.mode === 'admin-lend';
  }

  get showScheduleButton(): boolean {
    return !this.isAdminMode && this.args.bookDeskEnabled;
  }

  // Cancel-lending mode with no dates to cancel would otherwise render an
  // unusable calendar (today disabled + endDate clamped to today = nothing
  // selectable). Show an explicit empty state so the admin sees why.
  get showCancelLendingEmptyState(): boolean {
    return this.isCancelLendingMode && (this.args.allowedDates ?? []).length === 0;
  }

  get modalTitle(): string {
    if (this.showDeskSelection) return 'Select desks to lend';
    return this.args.title ?? 'Select multiple dates';
  }

  get submitLabel(): string {
    if (this.args.submitLabel) return this.args.submitLabel;
    if (this.isCancelLendingMode) return 'Cancel lending';
    return 'Lend desk';
  }

  get noSelection(): boolean {
    return this.selectedDates.length === 0;
  }

  get currentMonthLabel(): string {
    return formatInTimeZone(this.currentMonth, this.effectiveTimeZone, 'MMM yyyy');
  }

  get formattedSelectedDates(): Array<{ date: Date; label: string }> {
    return [...this.selectedDates]
      .sort((a, b) => a.getTime() - b.getTime())
      .map((date) => ({
        date,
        label: formatInTimeZone(date, this.effectiveTimeZone, 'MMM d, yyyy'),
      }));
  }

  @cached
  get daysToCustomize(): DayConfig[] {
    if (this.isCancelLendingMode) {
      return this.cancelLendingDaysToCustomize;
    }

    const configs: DayConfig[] = [];

    const { days, lendingStatusByDate, lendingStatusByDeskAndDate } = this.args;
    const hasPerDeskData = lendingStatusByDeskAndDate && Object.keys(lendingStatusByDeskAndDate).length > 0;

    for (const day of days) {
      const date = day.date instanceof Date ? day.date : new Date(day.date);
      const dateKey = this.formatDateKey(date);
      const unixTimestamp = getUnixTime(startOfDay(date));

      const hasInvite = !!day.screeningCard?.id;
      const hasReservations = (day.reservations && day.reservations.length > 0) || !!day.reservation;
      const isAllDesksBooked = hasPerDeskData
        ? Object.values(lendingStatusByDeskAndDate).every((dates) => dates[dateKey]?.lendingStatus === 'LENT_BOOKED')
        : lendingStatusByDate[dateKey]?.lendingStatus === 'LENT_BOOKED';

      if (hasInvite || hasReservations || isAllDesksBooked) {
        let tooltip = '';
        if (isAllDesksBooked) {
          tooltip = this.args.multiDeskEnabled
            ? 'All your desks are lent and booked by someone else for this day'
            : 'Your desk is lent and booked by someone else for this day';
        } else if (hasReservations) {
          tooltip = 'You already have a reservation for this day';
        } else if (hasInvite) {
          tooltip = 'You are already scheduled for this day';
        }

        configs.push({
          date: unixTimestamp,
          disabled: true,
          class: 'opacity-40 multi-date-disabled',
          tooltip,
        });
      }
    }

    if (this.isAdminMode) {
      configs.push(this.adminTodayDisabledConfig);
    }

    if (this.isAdminLendMode) {
      const tz = this.effectiveTimeZone;
      const referenceNow = new Date();
      for (const dateKey of this.args.adminLendDisabledDates ?? []) {
        const cellDate = parseYyyyMmDdInTimeZone(dateKey, referenceNow, tz);
        configs.push({
          date: getUnixTime(cellDate),
          disabled: true,
          class: 'opacity-40 multi-date-disabled',
          tooltip: 'Desk is already lent on this day',
        });
      }
    }

    return configs;
  }

  // Admins can't lend/cancel for today — backend rejects today + past for admin ops.
  // We both disable the cell here (visual) and guard toggleDate (defense).
  get adminTodayDisabledConfig(): DayConfig {
    return {
      date: this.locationTodayUnix,
      disabled: true,
      class: 'opacity-40 multi-date-disabled',
      tooltip: "Can't select today — pick a future date",
    };
  }

  // Walk [today+1, max allowed key] in the effective TZ and emit a disabled
  // DayConfig for every day not in @allowedDates. `endDate` in dateLimits also
  // caps the range, but the in-range non-allowed cells still need styling +
  // click-blocking via DayConfig. Bounded by differenceInCalendarDays so the
  // loop length is deterministic and can't drift.
  @cached
  get cancelLendingDaysToCustomize(): DayConfig[] {
    const configs: DayConfig[] = [this.adminTodayDisabledConfig];
    const allowed = new Set(this.args.allowedDates ?? []);

    const tz = this.effectiveTimeZone;
    const todayDate = parseYyyyMmDdInTimeZone(this.locationTodayKey, new Date(), tz);
    const maxDate = parseYyyyMmDdInTimeZone(this.cancelLendingMaxKey, new Date(), tz);
    const daysInWindow = differenceInCalendarDays(maxDate, todayDate);

    for (let i = 1; i <= daysInWindow; i++) {
      const cursorDate = addDays(todayDate, i);
      const cursorKey = this.formatDateKey(cursorDate);
      if (allowed.has(cursorKey)) continue;

      configs.push({
        date: getUnixTime(cursorDate),
        disabled: true,
        class: 'opacity-40 multi-date-disabled',
        tooltip: 'Desk is not lent on this day',
      });
    }

    return configs;
  }

  get deskOptions(): DeskOption[] {
    return (this.args.lendableDesks ?? []).map((desk) => ({
      deskId: String(desk.deskId),
      label: `${desk.deskName}, ${desk.floorName}`,
    }));
  }

  get noDeskSelection(): boolean {
    return this.selectedDesks.length === 0;
  }

  lendTask = dropTask(async () => {
    // Desk-selection diversion is for multi-desk LEND flows only. In
    // admin-cancel-lending the caller targets a single desk (the one whose
    // popup opened the modal) and @onCancelLend takes dates only, so forcing
    // desk selection would be a dead-end step.
    if (this.args.multiDeskEnabled && !this.showDeskSelection && !this.isCancelLendingMode) {
      this.showDeskSelection = true;
      return;
    }

    const dates = this.selectedDates.map((d) => this.formatDateKey(d));

    // Silently falling back to @onLend here would invert admin intent —
    // we'd create lendings on the dates the admin picked to cancel. Assert
    // outside the catch below so the failure surfaces as an Ember error
    // rather than being swallowed alongside handler rejections.
    if (this.isCancelLendingMode) {
      assert(
        'MultiDateScheduleModal: @onCancelLend is required when @mode="admin-cancel-lending"',
        typeof this.args.onCancelLend === 'function',
      );
    }

    try {
      if (this.isCancelLendingMode) {
        await this.args.onCancelLend!(dates);
      } else {
        const deskIds = this.args.multiDeskEnabled ? this.selectedDesks.map((d) => d.deskId) : undefined;
        await this.args.onLend(dates, deskIds);
      }
    } catch (handlerError) {
      // Handler signaled failure (already showed a user-visible flash).
      // Skip onClose() so the admin's date selection survives a retry.
      // Log so silent rejections aren't invisible to debuggers.
      // eslint-disable-next-line no-console
      console.warn('multi-date-schedule-modal: handler threw, keeping modal open', handlerError);
      return;
    }
    this.args.onClose();
  });

  scheduleTask = dropTask(async () => {
    const dates = this.selectedDates.map((d) => this.formatDateKey(d));
    await this.args.onSchedule(dates);
    this.args.onClose();
  });

  @action
  onDesksChange(selected: DeskOption[]): void {
    this.selectedDesks = selected;
  }

  @action
  backToDates(): void {
    this.showDeskSelection = false;
    this.selectedDesks = [];
  }

  @action
  toggleCalendar(): void {
    this.calendarOpen = !this.calendarOpen;
  }

  @action
  toggleDate(date: Date): void {
    const cellKey = this.formatDateKey(date);
    const todayKey = this.locationTodayKey;

    if (cellKey < todayKey) return;
    if (this.isAdminMode && cellKey === todayKey) return;

    if (this.isCancelLendingMode) {
      const allowed = new Set(this.args.allowedDates ?? []);
      if (!allowed.has(cellKey)) return;
    }

    if (this.isAdminLendMode) {
      const disabled = new Set(this.args.adminLendDisabledDates ?? []);
      if (disabled.has(cellKey)) return;
    }

    const { activeDays } = this.args;
    if (activeDays && activeDays.length > 0) {
      const dayOfWeek = formatInTimeZone(date, this.effectiveTimeZone, 'EEEE');
      if (!activeDays.some((d) => d.dayOfWeek === dayOfWeek)) return;
    }

    const existing = this.selectedDates.find((d) => isSameDay(d, date));
    if (existing) {
      this.selectedDates = this.selectedDates.filter((d) => !isSameDay(d, date));
    } else {
      this.selectedDates = [...this.selectedDates, date];
    }
  }

  @action
  removeDate(date: Date, e: MouseEvent): void {
    e.stopPropagation();
    e.preventDefault();
    this.selectedDates = this.selectedDates.filter((d) => !isSameDay(d, date));
  }

  @action
  previousMonth(): void {
    this.currentMonth = subMonths(this.currentMonth, 1);
  }

  @action
  nextMonth(): void {
    this.currentMonth = addMonths(this.currentMonth, 1);
  }

  <template>
    <ModalDialog
      @clickOutsideToClose={{true}}
      @onClose={{@onClose}}
      @translucentOverlay={{true}}
      @targetAttachment="center"
    >
      <div class="p-6" style="min-width: 400px;" data-test-multi-date-schedule-modal>
        <div class="flex justify-between items-center mb-4">
          <h2 class="text-xl font-semibold">
            {{this.modalTitle}}
          </h2>
          <button
            type="button"
            class="btn btn--small btn--outline btn--cancel"
            aria-label="close"
            {{on "click" @onClose}}
            data-test-multi-date-close
          >
            &times;
          </button>
        </div>

        {{#if this.showDeskSelection}}
          <div class="mb-4">
            <PowerSelectMultiple
              @options={{this.deskOptions}}
              @selected={{this.selectedDesks}}
              @onChange={{this.onDesksChange}}
              @searchEnabled={{false}}
              @placeholder="Select desks"
              @dropdownClass="in-modal"
              data-test-multi-date-desk-select
              as |option|
            >
              {{option.label}}
            </PowerSelectMultiple>
          </div>

          <div class="flex justify-end gap-3">
            <button
              type="button"
              class="btn btn--small btn--outline"
              {{on "click" this.backToDates}}
              data-test-multi-date-back
            >
              Back
            </button>
            <button
              type="button"
              class="btn btn--small btn--brand"
              disabled={{or this.noDeskSelection this.lendTask.isRunning}}
              {{on "click" this.lendTask.perform}}
              data-test-multi-date-confirm-lend
            >
              {{if this.lendTask.isRunning "Submitting..." this.submitLabel}}
            </button>
          </div>
        {{else if this.showCancelLendingEmptyState}}

          <p class="text-carbon-60 mb-4" data-test-cancel-lending-empty>
            This desk has no upcoming lent dates to cancel.
          </p>
          <div class="flex justify-end">
            <button
              type="button"
              class="btn btn--small btn--brand"
              {{on "click" @onClose}}
              data-test-multi-date-dismiss-empty
            >
              Close
            </button>
          </div>

        {{else}}

          {{! Date select combobox }}
          <div class="mb-4">
            <div
              class="w-full flex items-center border border-carbon-20 rounded-md px-3 py-2 bg-white text-left cursor-pointer focus:ring-2 focus:ring-gem-50"
              {{on "click" this.toggleCalendar}}
              data-test-date-select-trigger
            >
              <div class="flex-1 flex flex-wrap gap-1 items-center min-h-[24px]">
                {{#if this.formattedSelectedDates.length}}
                  {{#each this.formattedSelectedDates as |entry|}}
                    <span class="inline-flex items-center bg-carbon-5 rounded px-2 py-0.5 text-sm" data-test-date-chip>
                      <button
                        type="button"
                        class="mr-1 text-base leading-none text-carbon-50 hover:text-carbon-90 cursor-pointer"
                        aria-label="Remove {{entry.label}}"
                        {{on "click" (fn this.removeDate entry.date)}}
                        data-test-remove-date
                      >
                        &times;
                      </button>
                      {{entry.label}}
                    </span>
                  {{/each}}
                {{else}}
                  <span class="text-carbon-40">Select dates</span>
                {{/if}}
              </div>
              <img
                src="/assets/images/chevron-down.svg"
                alt=""
                class="ml-2 w-3 h-3 transition-transform {{if this.calendarOpen 'rotate-180'}}"
              />
            </div>

            {{#if this.calendarOpen}}
              <div
                class="mt-2 p-3 border border-carbon-20 rounded-md multi-date-calendar-grid"
                data-test-calendar-dropdown
              >
                <div class="flex items-center justify-between mb-2">
                  <button
                    type="button"
                    class="btn btn--small btn--outline !border-none"
                    {{on "click" this.previousMonth}}
                    data-test-prev-month
                  >
                    &larr;
                  </button>
                  <span class="font-medium text-sm">
                    {{this.currentMonthLabel}}
                  </span>
                  <button
                    type="button"
                    class="btn btn--small btn--outline !border-none"
                    {{on "click" this.nextMonth}}
                    data-test-next-month
                  >
                    &rarr;
                  </button>
                </div>

                <CalendarGrid
                  @selectedDates={{this.selectedDates}}
                  @onSelectDate={{this.toggleDate}}
                  @daysToCustomize={{this.daysToCustomize}}
                  @dateLimits={{this.dateLimits}}
                  @currentMonth={{this.currentMonth}}
                  @activeDays={{@activeDays}}
                  @respectSchedulingLimits={{true}}
                  @timezone={{this.effectiveTimeZone}}
                />
              </div>
            {{/if}}
          </div>

          <div class="flex justify-end gap-3">
            {{#if @deskLendingEnabled}}
              <button
                type="button"
                class="btn btn--small {{if this.isAdminMode 'btn--brand' 'btn--outline'}}"
                disabled={{or this.noSelection this.lendTask.isRunning this.scheduleTask.isRunning}}
                {{on "click" this.lendTask.perform}}
                data-test-multi-date-lend
              >
                {{if this.lendTask.isRunning "Submitting..." this.submitLabel}}
              </button>
            {{/if}}
            {{#if this.showScheduleButton}}
              <button
                type="button"
                class="btn btn--small btn--brand"
                disabled={{or this.noSelection this.lendTask.isRunning this.scheduleTask.isRunning}}
                {{on "click" this.scheduleTask.perform}}
                data-test-multi-date-schedule
              >
                {{if this.scheduleTask.isRunning "Scheduling..." "Schedule"}}
              </button>
            {{/if}}
          </div>

        {{/if}}{{! end showDeskSelection else branch }}
      </div>
    </ModalDialog>
  </template>
}
